import React, { useState, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Upload, Loader, Save, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const StructuredEvidenceForm = ({
  schema,
  initialValues = {},
  fieldErrors = {},
  onSaveDraft,
  onSubmit,
  onFileUpload,
  submitting = false,
}) => {
  const [fieldValues, setFieldValues] = useState(() => {
    // Initialize from existing human submission (not AI-generated)
    const init = {};
    (schema?.fields || []).forEach((f) => {
      init[f.key] = initialValues[f.key] ?? (f.type === 'number' ? '' : '');
    });
    return init;
  });
  const [uploadingField, setUploadingField] = useState(null);
  const fileInputRefs = useRef({});

  const setField = (key, value) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = async (fieldKey, fieldLabel, file) => {
    if (!file) return;
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    const allowedTypes = ['.png', '.jpg', '.jpeg', '.pdf'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      toast.error('Only PNG, JPG, and PDF files are allowed');
      return;
    }
    setUploadingField(fieldKey);
    try {
      const evidenceId = await onFileUpload(fieldKey, file, fieldLabel);
      setField(fieldKey, evidenceId);
      toast.success('File uploaded');
    } catch {
      toast.error('Failed to upload file');
    } finally {
      setUploadingField(null);
    }
  };

  const renderField = (field) => {
    const { key, label, type, required, min_length } = field;
    const value = fieldValues[key] ?? '';
    const error = fieldErrors[key];
    const charCount = typeof value === 'string' ? value.length : 0;

    const baseInputClass =
      'w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ' +
      (error ? 'border-red-400 bg-red-50' : 'border-gray-300');

    return (
      <div key={key} className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>

        {type === 'date' && (
          <DatePicker
            selected={value ? new Date(value + 'T00:00:00') : null}
            onChange={(date) => {
              if (!date) return setField(key, '');
              const y = date.getFullYear();
              const m = String(date.getMonth() + 1).padStart(2, '0');
              const d = String(date.getDate()).padStart(2, '0');
              setField(key, `${y}-${m}-${d}`);
            }}
            maxDate={new Date()}
            dateFormat="yyyy-MM-dd"
            placeholderText="Select a past date"
            className={baseInputClass}
          />
        )}

        {type === 'textarea' && (
          <>
            <textarea
              value={value}
              onChange={(e) => setField(key, e.target.value)}
              rows={4}
              placeholder={min_length ? `Minimum ${min_length} characters required` : ''}
              className={baseInputClass + ' resize-y'}
            />
            {min_length && (
              <p className={`text-xs ${charCount >= min_length ? 'text-green-600' : 'text-gray-400'}`}>
                {charCount}/{min_length} characters minimum
              </p>
            )}
          </>
        )}

        {type === 'text' && (
          <input
            type="text"
            value={value}
            onChange={(e) => setField(key, e.target.value)}
            className={baseInputClass}
          />
        )}

        {type === 'number' && (
          <input
            type="number"
            value={value}
            onChange={(e) => setField(key, e.target.value)}
            className={baseInputClass}
          />
        )}

        {type === 'file' && (
          <div>
            <input
              ref={(el) => { fileInputRefs.current[key] = el; }}
              type="file"
              accept=".png,.jpg,.jpeg,.pdf"
              onChange={(e) => handleFileChange(key, label, e.target.files[0] || null)}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
              disabled={uploadingField === key}
            />
            <p className="mt-1 text-xs text-gray-500">PNG, JPG, or PDF. Max 10MB.</p>
            {uploadingField === key && (
              <p className="text-xs text-teal-600 flex items-center gap-1 mt-1">
                <Loader className="w-3 h-3 animate-spin" /> Uploading...
              </p>
            )}
            {value && uploadingField !== key && (
              <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                <CheckCircle className="w-3 h-3" /> File uploaded
              </p>
            )}
          </div>
        )}

        {error && (
          <p className="text-xs text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {error}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {(schema?.fields || []).map(renderField)}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={() => onSaveDraft(fieldValues)}
          disabled={submitting || uploadingField !== null}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save Draft
        </button>
        <button
          type="button"
          onClick={() => onSubmit(fieldValues)}
          disabled={submitting || uploadingField !== null}
          className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? (
            <><Loader className="w-4 h-4 animate-spin" /> Submitting...</>
          ) : (
            <><CheckCircle className="w-4 h-4" /> Submit Evidence</>
          )}
        </button>
      </div>
    </div>
  );
};

export default StructuredEvidenceForm;
